"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Send,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  Building,
  Check,
  X,
  Wallet,
  DollarSign,
  Globe,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { getBalance, USDC_ADDRESS } from "../services/authapi";
import { ethers } from "ethers";
import toast, { Toaster } from "react-hot-toast";
import { useSmartWallets } from "@privy-io/react-auth/smart-wallets";
import { useSearchParams } from "react-router-dom";

// Polyfill Buffer for browser environment
import { Buffer } from "buffer";
if (typeof window !== "undefined") {
  window.Buffer = Buffer;
}


interface Recipient {
  id: string;
  name?: string;
  username?: string;
  email?: string | null;
  phone?: string | null;
  wallet: string;
}

export function TransferPage() {
  const [amount, setAmount] = useState("");
  const [isMaxSelected, setIsMaxSelected] = useState(false);
  const { client } = useSmartWallets() as any;
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(
    null
  );
  const [recipientType, setRecipientType] = useState<
    "user" | "vendor" | "external"
  >("user");
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<Recipient[]>([]);
  const [vendors, setVendors] = useState<Recipient[]>([]);
  const [filteredResults, setFilteredResults] = useState<Recipient[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const user = useSelector((state: RootState) => state.user) as any;
  const senderWallet = user?.smartWallet || "";
  const senderUsername = user?.username || "User";

  // Read URL query parameters using React Router DOM
  const [searchParams] = useSearchParams();
  const urlToAddress = searchParams.get("to");
  const urlAmount = searchParams.get("amount");

  // Auto-fill from Coinbase Offramp when URL has ?to=...&amount=...
  useEffect(() => {
    if (urlToAddress && ethers.isAddress(urlToAddress) && urlAmount) {
      const parsedAmount = parseFloat(urlAmount);
      if (!isNaN(parsedAmount) && parsedAmount > 0) {
        setRecipientType("external");
        setSearchQuery(urlToAddress);
        setAmount(parsedAmount.toFixed(2));
        toast.success("Coinbase withdrawal details loaded — ready to send!");
      }
    }
  }, [urlToAddress, urlAmount]);

  const sendEmail = async (
    sender_email: string,
    receiver_email: string,
    sender_name: string,
    receiver_name: string,
    endpoint: string,
    transaction_details?: { [key: string]: string }
  ) => {
    console.log("Sending email", {
      sender_email,
      receiver_email,
      sender_name,
      receiver_name,
      transaction_details,
    });
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SERVERURL}/${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            sender_email,
            receiver_email,
            sender_name,
            receiver_name,
            transaction_details,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`${endpoint} email sent:`, data);
    } catch (err: any) {
      console.error(`Failed to send ${endpoint} email:`, err);
      toast.error(`Failed to send email: ${err.message}`);
    }
  };

  useEffect(() => {
    const fetchBalances = async () => {
      if (!senderWallet) {
        setError("No sender wallet address found.");
        toast.error("No sender wallet address found.");
        return;
      }
      setLoading(true);
      try {
        const usdcBal = await getBalance(senderWallet);
        setWalletBalance(usdcBal);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching balances:", err.message);
        toast.error("Failed to fetch balances.");
        setError("Failed to fetch balances: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBalances();
  }, [senderWallet]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          username: doc.data().username,
          email: doc.data().email || null,
          phone: doc.data().phone || null,
          wallet: doc.data().wallet,
        }));
        setUsers(usersData);

        const vendorsSnapshot = await getDocs(collection(db, "vendors"));
        const vendorsData = vendorsSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
          wallet: doc.data().wallet,
        }));
        setVendors(vendorsData);
      } catch (err: any) {
        console.error("Error fetching users/vendors:", err);
        toast.error("Failed to fetch users or vendors.");
        setError("Failed to fetch users or vendors: " + err.message);
      }
    };
    fetchData();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setSearchQuery(value);
    setSelectedRecipient(null);

    if (recipientType === "external") {
      setShowDropdown(false);
      return;
    }

    const searchTerm = value.toLowerCase();
    const list = recipientType === "user" ? users : vendors;
    const filtered = list.filter((item) => {
      if (recipientType === "user") {
        return (
          (item.username?.toLowerCase().includes(searchTerm) ||
            item.email?.toLowerCase().includes(searchTerm) ||
            item.phone?.toLowerCase().includes(searchTerm) ||
            item.wallet.toLowerCase().includes(searchTerm)) &&
          item.wallet !== senderWallet
        );
      } else {
        return (
          item.name?.toLowerCase().includes(searchTerm) ||
          item.wallet.toLowerCase().includes(searchTerm)
        );
      }
    });
    setFilteredResults(filtered);
    setShowDropdown(value.length >= 2 && filtered.length > 0);
  };

  const handleInputFocus = () => {
    if (recipientType === "external") return;

    if (searchQuery.length >= 2) {
      const searchTerm = searchQuery.toLowerCase();
      const list = recipientType === "user" ? users : vendors;
      const filtered = list.filter((item) => {
        if (recipientType === "user") {
          return (
            item.username?.toLowerCase().includes(searchTerm) ||
            item.email?.toLowerCase().includes(searchTerm) ||
            item.phone?.toLowerCase().includes(searchTerm) ||
            item.wallet.toLowerCase().includes(searchTerm)
          );
        } else {
          return (
            item.name?.toLowerCase().includes(searchTerm) ||
            item.wallet.toLowerCase().includes(searchTerm)
          );
        }
      });
      setFilteredResults(filtered);
      setShowDropdown(filtered.length > 0);
    }
  };

  const handleSelectRecipient = (selected: Recipient) => {
    setSelectedRecipient(selected);
    setSearchQuery(
      recipientType === "user" ? selected.username! : selected.name!
    );
    setShowDropdown(false);
  };

  const confirmTransfer = async () => {
    const recipientAddress = selectedRecipient
      ? selectedRecipient.wallet
      : searchQuery;

    try {
      setLoading(true);

      const transferInterface = new ethers.Interface([
        "function transfer(address to, uint256 amount) returns (bool)",
      ]);

      const amountWei = ethers.parseUnits(amount, 6);
      let feeWei = 0n;

      // 1% fee for user-to-user AND external transfers
      if (recipientType === "user" || recipientType === "external") {
        feeWei = amountWei / 100n;
      }

      const calls = [
        {
          to: USDC_ADDRESS,
          data: transferInterface.encodeFunctionData("transfer", [
            recipientAddress,
            amountWei,
          ]),
        },
      ];

      if (feeWei > 0n) {
        calls.push({
          to: USDC_ADDRESS,
          data: transferInterface.encodeFunctionData("transfer", [
            import.meta.env.VITE_Admin_Wallet,
            feeWei,
          ]),
        });
      }

      const txHash = await client.sendTransaction({
        chain: 8453,
        calls,
      });

      toast.success(`Transfer sent! Tx: ${txHash}`);

      if (user?.email) {
        const receiverName = selectedRecipient
          ? selectedRecipient.username || selectedRecipient.name || "Recipient"
          : truncateAddress(recipientAddress);
        const transactionDate = new Date().toISOString().split("T")[0];

        await sendEmail(
          user.email,
          "support@cashtap.cash",
          senderUsername,
          receiverName,
          "complete-transfer",
          {
            AMOUNT: amountNum.toFixed(2),
            SENDER: senderUsername,
            TRANSFERED_TO: receiverName,
            TRANSACTION_ID: txHash,
            DATE: transactionDate,
          }
        );
      }

      const usdcBal = await getBalance(senderWallet);
      setWalletBalance(usdcBal);

      setAmount("");
      setSearchQuery("");
      setSelectedRecipient(null);
      setShowModal(false);
      setIsMaxSelected(false);

      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);
    } catch (err: any) {
      console.error("Error sending transfer:", err.message);
      let errorMessage = "Failed to send transfer: " + err.message;
      if (err.message.includes("sponsorship")) {
        errorMessage =
          "Gas sponsorship failed. Ensure sufficient credits in Privy dashboard.";
      } else if (err.message.includes("insufficient funds")) {
        errorMessage = "Insufficient USDC balance or sponsorship credits.";
      } else if (err.message.includes("wallet")) {
        errorMessage =
          "Wallet error. Ensure you're using a Privy embedded wallet.";
      }
      toast.error(errorMessage);
      setError(errorMessage);
      setShowModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const recipientAddress = selectedRecipient
      ? selectedRecipient.wallet
      : searchQuery;

    if (!ethers.isAddress(recipientAddress)) {
      toast.error("Invalid recipient address");
      return;
    }

    if (!ethers.isAddress(senderWallet)) {
      toast.error("Invalid sender wallet address");
      return;
    }

    const adminWallet = import.meta.env.VITE_Admin_Wallet;
    if (!ethers.isAddress(adminWallet)) {
      toast.error("Invalid admin wallet address");
      return;
    }

    let amountWei: bigint;
    try {
      amountWei = ethers.parseUnits(amount, 6);
    } catch {
      toast.error("Invalid amount");
      return;
    }

    if (amountWei <= 0n) {
      toast.error("Amount must be greater than 0");
      return;
    }

    const minAmountWei = ethers.parseUnits("0.5", 6);
    if (amountWei < minAmountWei) {
      toast.error("Minimum transfer amount is 0.5 USDC");
      return;
    }

    let feeWei = 0n;
    if (recipientType === "user" || recipientType === "external") {
      feeWei = amountWei / 100n;
    }

    const totalWei = amountWei + feeWei;

    const balanceWei = ethers.parseUnits(walletBalance, 6);
    if (totalWei > balanceWei) {
      toast.error("Insufficient USDC balance for amount + fee");
      return;
    }

    if (!client) {
      toast.error(
        "No smart wallet client found. Please connect a Privy wallet."
      );
      return;
    }

    setShowModal(true);
  };

  function truncateAddress(address: string): string {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  const shouldShowUpward = () => {
    if (!inputRef.current) return false;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    return spaceBelow < 240;
  };

  const isExternal =
    recipientType === "external" && ethers.isAddress(searchQuery);
  const toType =
    recipientType === "user"
      ? "CashTap User"
      : recipientType === "vendor"
      ? "Vendor"
      : "External Wallet";
  const recipientName = selectedRecipient
    ? selectedRecipient.username ||
      selectedRecipient.name ||
      truncateAddress(selectedRecipient.wallet)
    : isExternal || recipientType === "external"
    ? truncateAddress(searchQuery)
    : "Select";
  const recipientAddress = selectedRecipient
    ? selectedRecipient.wallet
    : searchQuery;

  const amountNum = parseFloat(amount) || 0;
  const hasFee = recipientType === "user" || recipientType === "external";
  const feeNum = hasFee ? amountNum * 0.01 : 0;
  const totalNum = amountNum + feeNum;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />

      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          Transfer Funds
        </h1>
        <p className="text-gray-400 text-lg">
          Send USDC to users, vendors, or any external wallet address
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-2xl">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-3">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-white mb-4">From Wallet</h3>
              <div className="p-6 bg-gray-800/30 rounded-2xl border border-[#61CE70]/20">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-[#61CE70]">CashTap Wallet</p>
                    <p className="text-gray-400 text-sm">USDC • Base Chain</p>
                  </div>
                  <p className="text-white font-bold text-lg">
                    {loading
                      ? "Loading..."
                      : `${parseFloat(walletBalance).toFixed(2)} USDC`}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-white mb-4">Transfer To</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setRecipientType("user");
                    setSearchQuery("");
                    setSelectedRecipient(null);
                    setShowDropdown(false);
                  }}
                  className={`p-6 rounded-2xl border transition-all duration-200 ${
                    recipientType === "user"
                      ? "border-[#61CE70] bg-[#61CE70]/10 shadow-lg shadow-[#61CE70]/20"
                      : "border-gray-700/50 bg-gray-800/30 hover:border-gray-600/50"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-3 rounded-xl ${
                        recipientType === "user"
                          ? "bg-[#61CE70]/20"
                          : "bg-gray-700/50"
                      }`}
                    >
                      <Users
                        className={`w-6 h-6 ${
                          recipientType === "user"
                            ? "text-[#61CE70]"
                            : "text-gray-400"
                        }`}
                      />
                    </div>
                    <div className="text-left">
                      <p
                        className={`font-medium ${
                          recipientType === "user"
                            ? "text-[#61CE70]"
                            : "text-white"
                        }`}
                      >
                        CashTap User
                      </p>
                      <p className="text-gray-400 text-sm">1% fee applies</p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRecipientType("vendor");
                    setSearchQuery("");
                    setSelectedRecipient(null);
                    setFilteredResults([]);
                    setShowDropdown(false);
                  }}
                  className={`p-6 rounded-2xl border transition-all duration-200 ${
                    recipientType === "vendor"
                      ? "border-[#61CE70] bg-[#61CE70]/10 shadow-lg shadow-[#61CE70]/20"
                      : "border-gray-700/50 bg-gray-800/30 hover:border-gray-600/50"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-3 rounded-xl ${
                        recipientType === "vendor"
                          ? "bg-[#61CE70]/20"
                          : "bg-gray-700/50"
                      }`}
                    >
                      <Building
                        className={`w-6 h-6 ${
                          recipientType === "vendor"
                            ? "text-[#61CE70]"
                            : "text-gray-400"
                        }`}
                      />
                    </div>
                    <div className="text-left">
                      <p
                        className={`font-medium ${
                          recipientType === "vendor"
                            ? "text-[#61CE70]"
                            : "text-white"
                        }`}
                      >
                        Vendor
                      </p>
                      <p className="text-gray-400 text-sm">No fee</p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRecipientType("external");
                    setSearchQuery("");
                    setSelectedRecipient(null);
                    setShowDropdown(false);
                  }}
                  className={`p-6 rounded-2xl border transition-all duration-200 ${
                    recipientType === "external"
                      ? "border-[#61CE70] bg-[#61CE70]/10 shadow-lg shadow-[#61CE70]/20"
                      : "border-gray-700/50 bg-gray-800/30 hover:border-gray-600/50"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-3 rounded-xl ${
                        recipientType === "external"
                          ? "bg-[#61CE70]/20"
                          : "bg-gray-700/50"
                      }`}
                    >
                      <Globe
                        className={`w-6 h-6 ${
                          recipientType === "external"
                            ? "text-[#61CE70]"
                            : "text-gray-400"
                        }`}
                      />
                    </div>
                    <div className="text-left">
                      <p
                        className={`font-medium ${
                          recipientType === "external"
                            ? "text-[#61CE70]"
                            : "text-white"
                        }`}
                      >
                        External Wallet
                      </p>
                      <p className="text-gray-400 text-sm">1% fee applies</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br z-[100] from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm relative">
              <h3 className="text-xl font-bold text-white mb-4">
                {recipientType === "external"
                  ? "Enter Wallet Address"
                  : "Select Recipient"}
              </h3>
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleInputFocus}
                  className="w-full px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all font-mono"
                  placeholder={
                    recipientType === "user"
                      ? "Enter PayzTapTag, Phone, Email"
                      : recipientType === "vendor"
                      ? "Enter Vendor Name or Address"
                      : "Enter EVM wallet address (0x...)"
                  }
                  required
                />
                {showDropdown && recipientType !== "external" && (
                  <div
                    role="listbox"
                    aria-label="Select recipient"
                    className={`absolute w-full bg-gradient-to-br from-[#1A2A3B] to-[#0D1419] border border-gray-700/50 rounded-3xl shadow-[0_0_10px_rgba(97,206,112,0.2)] z-[1000] transition-all duration-200 overflow-hidden ${
                      shouldShowUpward()
                        ? "bottom-12 top-auto"
                        : "top-full mt-2"
                    } max-h-60 overflow-y-auto`}
                  >
                    {filteredResults.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleSelectRecipient(item)}
                        className="p-4 hover:bg-[#61CE70]/5 cursor-pointer flex items-center justify-between border-b border-gray-700/50 last:border-b-0 transition-colors duration-200"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-[#61CE70]/20 rounded-lg">
                            {recipientType === "user" ? (
                              <Users className="w-4 h-4 text-[#61CE70]" />
                            ) : (
                              <Building className="w-4 h-4 text-[#61CE70]" />
                            )}
                          </div>
                          <div>
                            <span className="text-white font-medium">
                              {recipientType === "user"
                                ? item.username
                                : item.name}
                            </span>
                            {recipientType === "user" && (
                              <p className="text-gray-400 text-xs">
                                {item.email || item.phone || "No contact info"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-400 text-sm">
                            Wallet: {truncateAddress(item.wallet)}
                          </span>
                          {selectedRecipient?.id === item.id && (
                            <Check className="w-5 h-5 text-[#61CE70]" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(selectedRecipient ||
                  (recipientType === "external" &&
                    ethers.isAddress(searchQuery))) && (
                  <div className="mt-2 p-3 bg-gray-800/30 rounded-xl border border-[#61CE70]/20 flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-[#61CE70]" />
                    <p className="text-gray-300 text-sm">
                      Recipient Wallet:{" "}
                      <span className="font-mono text-white">
                        {truncateAddress(recipientAddress)}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-white mb-4">Amount</h3>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setIsMaxSelected(false);
                  }}
                  className="w-full px-4 py-4 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white text-lg placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                  placeholder="0.0"
                  min="0.5"
                  max={parseFloat(walletBalance)}
                  step="0.1"
                  required
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">
                  USDC
                </span>
              </div>
              <div className="flex justify-between mt-2">
                <p className="text-gray-400 text-sm">
                  Available: {parseFloat(walletBalance).toFixed(2)} USDC
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const bal = parseFloat(walletBalance);
                    const KEEP_IN_WALLET = 0.02;
                    const MIN_TRANSFER = 0.5;

                    let available = bal - KEEP_IN_WALLET;

                    if (hasFee) {
                      available = (bal - KEEP_IN_WALLET) / 1.01;
                    }

                    if (available < MIN_TRANSFER) {
                      toast.error(
                        `Not enough balance for a ${MIN_TRANSFER} USDC transfer`
                      );
                      return;
                    }

                    setAmount(available.toFixed(2));
                    setIsMaxSelected(true);
                  }}
                  className="text-[#61CE70] text-sm hover:text-[#50B060] transition-colors"
                >
                  Max
                </button>
              </div>
              {isMaxSelected && (
                <p className="text-yellow-400 text-sm mt-2">
                  {hasFee
                    ? "This amount includes a 1% fee."
                    : "This amount includes no fees."}
                </p>
              )}
            </div>

            <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-4 sm:p-8 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-white mb-4">
                Transfer Summary
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between w-full p-6 bg-gray-900/40 rounded-2xl shadow-lg border border-gray-700">
                  <div className="flex flex-col items-center text-center flex-1">
                    <p className="text-[#61CE70] font-semibold text-sm tracking-wide">
                      FROM
                    </p>
                    <p className="text-white mt-1 text-xs md:text-base">
                      Personal Wallet
                    </p>
                  </div>
                  <div className="flex items-center justify-center mx-6">
                    <ArrowRight className="text-gray-400 w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                  <div className="flex flex-col items-center text-center flex-1">
                    <p className="text-blue-400 font-semibold text-sm tracking-wide">
                      TO
                    </p>
                    <p className="text-white mt-1 text-xs md:text-base font-bold uppercase">
                      {recipientName}
                    </p>
                    <p className="text-gray-300 text-xs mt-0.5">{toType}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Transfer Amount</span>
                    <span className="text-white">
                      {amountNum.toFixed(2)} USDC
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">
                      Fee {hasFee ? "(1%)" : ""}
                    </span>
                    <span className="text-white">{feeNum.toFixed(2)} USDC</span>
                  </div>
                  <div className="border-t border-gray-700/50 pt-3">
                    <div className="flex justify-between">
                      <span className="text-white font-medium">Total Cost</span>
                      <span className="text-[#61CE70] font-bold text-lg">
                        {totalNum.toFixed(2)} USDC
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-700/50">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-blue-400" />
                      <span className="text-gray-300">5-10 minutes</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#61CE70]" />
                      <span className="text-gray-300">Secured transaction</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="w-5 h-5 text-yellow-400" />
                      <span className="text-gray-300">
                        Verify recipient carefully
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={
                !amount || !searchQuery || totalNum > parseFloat(walletBalance)
              }
              className="w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white py-4 rounded-2xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex items-center justify-center space-x-2"
            >
              <Send className="w-5 h-5" />
              <span>{loading ? "Processing..." : "Send Transfer"}</span>
            </button>
          </form>
        </div>
      </div>

      {showModal && (
        <div className="fixed z-[1000] inset-0 bg-black/70 flex items-center justify-center">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-[#61CE70]/30 rounded-3xl p-8 backdrop-blur-md max-w-md w-full relative shadow-2xl shadow-[#61CE70]/10">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-[#61CE70] transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <CheckCircle className="w-6 h-6 text-[#61CE70]" />
              <span>Confirm Transfer</span>
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Wallet className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">From:</span>
                </div>
                <span className="text-white font-medium">
                  PayzTap Wallet (
                  <span className="text-[#61CE70]"> {senderUsername} </span>)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {toType === "CashTap User" ? (
                    <Users className="w-5 h-5 text-[#61CE70]" />
                  ) : toType === "Vendor" ? (
                    <Building className="w-5 h-5 text-[#61CE70]" />
                  ) : (
                    <Globe className="w-5 h-5 text-[#61CE70]" />
                  )}
                  <span className="text-gray-400">To:</span>
                </div>
                <span className="text-white font-medium">
                  {recipientName} ({toType})
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">Transfer Amount:</span>
                </div>
                <span className="text-white">{amountNum.toFixed(2)} USDC</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-gray-400">
                    Fees {hasFee ? "(1%)" : ""}:
                  </span>
                </div>
                <span className="text-white">{feeNum.toFixed(2)} USDC</span>
              </div>
              <div className="border-t border-[#61CE70]/20 pt-3 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-[#61CE70]" />
                  <span className="text-white font-medium">Total Amount:</span>
                </div>
                <span className="text-[#61CE70] font-bold">
                  {totalNum.toFixed(2)} USDC
                </span>
              </div>
            </div>
            <div className="mt-8 flex w-full justify-center space-x-2">
              <button
                onClick={() => setShowModal(false)}
                className="w-1/2 text-center justify-center py-3 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-all flex items-center space-x-2"
              >
                <X className="w-4 h-4 bg-white text-red-500 rounded-lg" />
                <span>Cancel</span>
              </button>
              <button
                onClick={confirmTransfer}
                disabled={loading}
                className="w-1/2 justify-center py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>{loading ? "Processing..." : "Confirm"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="fixed z-[1000] inset-0 bg-black/70 flex items-center justify-center">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-[#61CE70]/30 rounded-3xl p-8 backdrop-blur-md max-w-sm w-full relative shadow-2xl shadow-[#61CE70]/10">
            <div className="flex flex-col items-center space-y-4">
              <CheckCircle className="w-12 h-12 text-[#61CE70]" />
              <h2 className="text-xl font-bold text-white">
                Transaction Successful
              </h2>
              <p className="text-gray-400 text-center">
                Your transfer has been completed successfully.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransferPage;
